import React, { createContext, useContext, useState, useCallback } from 'react';

interface LoadingState {
  [key: string]: boolean;
}

interface LoadingContextType {
  loadingStates: LoadingState;
  setLoading: (key: string, isLoading: boolean) => void;
  isLoading: (key: string) => boolean;
  isAnyLoading: () => boolean;
  clearAllLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

/**
 * Enhanced Loading Provider
 * Manages loading states across the entire dashboard application
 */
export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<LoadingState>({});

  const setLoading = useCallback((key: string, isLoading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: isLoading
    }));
  }, []);

  const isLoading = useCallback((key: string) => {
    return loadingStates[key] || false;
  }, [loadingStates]);

  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(Boolean);
  }, [loadingStates]);

  const clearAllLoading = useCallback(() => {
    setLoadingStates({});
  }, []);

  return (
    <LoadingContext.Provider value={{
      loadingStates,
      setLoading,
      isLoading,
      isAnyLoading,
      clearAllLoading
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

/**
 * Higher-order component for automatic loading states
 */
export const withLoading = <P extends object>(
  Component: React.ComponentType<P>,
  loadingKey?: string
) => {
  return React.forwardRef<any, P>((props, ref) => {
    const { setLoading } = useLoading();
    const key = loadingKey || Component.displayName || Component.name || 'component';

    React.useEffect(() => {
      setLoading(key, true);
      const timer = setTimeout(() => setLoading(key, false), 100);
      return () => clearTimeout(timer);
    }, [setLoading, key]);

    return <Component {...(props as P)} />;
  });
};

/**
 * Loading states for common dashboard operations
 */
export const LOADING_KEYS = {
  // Dashboard loading
  DASHBOARD_ADMIN: 'dashboard.admin',
  DASHBOARD_PROJECT_MANAGER: 'dashboard.project_manager',
  DASHBOARD_DEVELOPER: 'dashboard.developer',
  DASHBOARD_CLIENT: 'dashboard.client',
  
  // Data fetching
  FETCH_PROJECTS: 'fetch.projects',
  FETCH_TASKS: 'fetch.tasks',
  FETCH_TEAM: 'fetch.team',
  FETCH_METRICS: 'fetch.metrics',
  FETCH_NOTIFICATIONS: 'fetch.notifications',
  
  // CRUD operations
  CREATE_PROJECT: 'create.project',
  UPDATE_PROJECT: 'update.project',
  DELETE_PROJECT: 'delete.project',
  CREATE_TASK: 'create.task',
  UPDATE_TASK: 'update.task',
  DELETE_TASK: 'delete.task',
  
  // File operations
  UPLOAD_FILE: 'upload.file',
  DOWNLOAD_FILE: 'download.file',
  
  // Authentication
  LOGIN: 'auth.login',
  LOGOUT: 'auth.logout',
  REGISTER: 'auth.register',
  
  // Navigation
  NAVIGATION_CHANGE: 'navigation.change',
} as const;

/**
 * Hook for managing async operations with loading states
 */
export const useAsyncOperation = () => {
  const { setLoading } = useLoading();

  return { setLoading };
};